import { useEffect, useMemo, useRef, useState } from 'react';
import './productHeroMetaSection.css';
import SimpleMarkdown from './SimpleMarkdown';

type ProductHeroMetaSectionProps = {
  readMoreIconSrc: string;
  fullDescription: string;
  productTitle: string;
  brandText: string;
  chips?: string[];
  partialTextMinChars?: number;
  purchaseUrl?: string;
  websiteUrl?: string;
  brandName?: string;
};

export default function ProductHeroMetaSection({
  readMoreIconSrc,
  fullDescription,
  productTitle,
  brandText,
  chips = [],
  partialTextMinChars = 90,
  purchaseUrl,
  websiteUrl,
  brandName,
}: ProductHeroMetaSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const partialDescription = useMemo(() => {
    const minChars = Math.max(1, partialTextMinChars);
    if (fullDescription.length <= minChars) {
      return fullDescription;
    }

    const roughCut = fullDescription.slice(0, minChars);
    const nearestWordBreak = roughCut.lastIndexOf(' ');
    const safeCutIndex = nearestWordBreak > Math.floor(minChars * 0.6) ? nearestWordBreak : minChars;
    return `${fullDescription.slice(0, safeCutIndex).trimEnd()}…`;
  }, [fullDescription, partialTextMinChars]);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    setNeedsReadMore(el.scrollHeight > 61);
  }, [fullDescription]);

  return (
    <section className="rid-expanded__product-meta" data-testid="landing-product-hero-meta">
      <h1 data-testid="landing-product-hero-title">{productTitle}</h1>
      <p className="rid-expanded__brand" data-testid="landing-product-hero-brand">{brandText}</p>
      {chips.length > 0 && (
        <div className="rid-expanded__chips">
          {chips.map((chip) => (
            <span key={chip}>{chip}</span>
          ))}
        </div>
      )}
      <div className="rid-expanded__description" ref={descriptionRef}>
        <SimpleMarkdown text={isExpanded ? fullDescription : (needsReadMore ? partialDescription : fullDescription)} />
      </div>
      {needsReadMore && (
        <button type="button" className="rid-expanded__read-more" onClick={() => setIsExpanded((prev) => !prev)} data-testid="landing-product-hero-read-more" data-expanded={isExpanded}>
          {isExpanded ? 'Read less' : 'Read more'}
          <img
            src={readMoreIconSrc}
            alt=""
            aria-hidden="true"
            className={`rid-expanded__read-more-icon ${isExpanded ? 'rid-expanded__read-more-icon--expanded' : ''}`}
          />
        </button>
      )}
      {(purchaseUrl || websiteUrl) && (
        <div className="rid-expanded__brand-links">
          {purchaseUrl && (
            <a href={purchaseUrl} target="_blank" rel="noopener noreferrer" className="rid-expanded__brand-link" data-testid="landing-product-hero-purchase-link">
              Purchase Products
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 1.5H10.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.5 1.5L1.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          )}
          {websiteUrl && (
            <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="rid-expanded__brand-link" data-testid="landing-product-hero-website-link">
              {brandName ? `${brandName}'s Website` : 'Website'}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3.5 1.5H10.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.5 1.5L1.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          )}
        </div>
      )}
    </section>
  );
}
